/* JZ Official — site behaviour (jzofficial.com, built from the V7 services theme).
   All JS lives here rather than inline in Liquid: a {% %} or {{ }} inside a
   <script> block is the failure mode Shopify refuses to parse, and an asset
   file cannot hit it. Sections pass data in via data- attributes or a
   type="application/json" block.

   Public hooks for section parts (appended after the main IIFE):
     window.JZTheme.reveal(root)        observe every .rv inside root (default document)
     window.JZTheme.init(selector, fn)  run fn(el) for each match now, and again for
                                            matches inside a section the theme editor re-renders
     window.JZTheme.qs / .qsa           querySelector helpers (qsa returns an Array)
     window.JZTheme.reducedMotion()     true when the visitor prefers reduced motion
*/
(function () {
  'use strict';

  var qs = function (s, r) { return (r || document).querySelector(s); };
  var qsa = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  var reducedMotion = function () {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };

  /* ---------------------------------------------------------
     Scroll reveal: .rv gains .in when it enters the viewport.
     CSS only hides .rv under html.js, so no-JS visitors see everything.
     --------------------------------------------------------- */
  var revealObserver = null;
  if ('IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px 20% 0px', threshold: 0 }  /* start before the element enters view, so it has finished fading in by the time it is seen */);
  }

  /* The CSS failsafe fades every .rv in after 2.5s unless html has .rv-on. If this script
     starts after that (slow network), show everything at once so nothing fades out and in again. */
  var revealLate = !!(window.performance && performance.now && performance.now() > 2400);
  document.documentElement.classList.add('rv-on');

  function reveal(root) {
    var scope = root || document;
    var items = qsa('.rv:not(.in)', scope);
    if (scope.classList && scope.classList.contains('rv') && !scope.classList.contains('in')) {
      items.push(scope);
    }
    items.forEach(function (el) {
      if (!revealObserver || reducedMotion() || revealLate) {
        el.classList.add('in');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* ---------------------------------------------------------
     init(selector, fn): run now, and again inside any section the theme
     editor re-renders (shopify:section:load), so section parts behave the
     same in the editor as on the live site.
     --------------------------------------------------------- */
  var inits = [];
  function init(selector, fn) {
    inits.push({ selector: selector, fn: fn });
    qsa(selector).forEach(function (el) {
      try { fn(el); } catch (err) { if (window.console) { console.error(err); } }
    });
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target;
    reveal(root);
    inits.forEach(function (item) {
      var found = qsa(item.selector, root);
      if (root.matches && root.matches(item.selector)) { found.push(root); }
      found.forEach(function (el) {
        try { item.fn(el); } catch (err) { if (window.console) { console.error(err); } }
      });
    });
  });

  window.JZTheme = {
    qs: qs,
    qsa: qsa,
    reveal: reveal,
    init: init,
    reducedMotion: reducedMotion
  };

  /* Start reveal first, so an error in any block below can never leave content hidden. */
  reveal(document);

  /* ---------------------------------------------------------
     Header: .is-scrolled past 8px, and --header-offset set to the rendered
     header height so anchor targets land below the sticky header.
     --------------------------------------------------------- */
  (function header() {
    var ticking = false;

    function paint() {
      ticking = false;
      var bar = qs('.site-header');
      if (bar) { bar.classList.toggle('is-scrolled', (window.pageYOffset || document.documentElement.scrollTop) > 8); }
    }
    function measure() {
      var bar = qs('.site-header');
      var h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
      if (h > 0) { document.documentElement.style.setProperty('--header-offset', h + 'px'); }
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(paint); }
    }, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    document.addEventListener('shopify:section:load', function () { paint(); measure(); });
    paint();
    measure();
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(measure); }
  }());

  /* ---------------------------------------------------------
     Mobile navigation drawer (delegated, so it survives the theme editor
     re-rendering the header).
     .nav-toggle[aria-controls] opens #nav-drawer; [data-nav-close], Escape and
     any link inside close it; Tab is kept inside the drawer while it is open.
     --------------------------------------------------------- */
  (function mobileNav() {
    var focusable = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

    function parts() {
      var toggle = qs('.nav-toggle');
      var drawer = toggle ? document.getElementById(toggle.getAttribute('aria-controls') || 'nav-drawer') : null;
      return { toggle: toggle, drawer: drawer, scrim: qs('.nav-scrim') };
    }
    function isOpen(p) { return !!(p.toggle && p.toggle.getAttribute('aria-expanded') === 'true'); }

    function setOpen(p, state, returnFocus) {
      if (!p.toggle || !p.drawer) { return; }
      p.toggle.setAttribute('aria-expanded', state ? 'true' : 'false');
      p.drawer.hidden = !state;
      if (p.scrim) { p.scrim.hidden = !state; }
      document.documentElement.classList.toggle('nav-open', state);
      if (state) {
        var first = qs('.nav-drawer__close', p.drawer) || qs(focusable, p.drawer);
        if (first) { first.focus(); }
      } else if (returnFocus) {
        p.toggle.focus();
      }
    }

    document.addEventListener('click', function (e) {
      if (!e.target.closest) { return; }
      var p = parts();
      if (!p.toggle || !p.drawer) { return; }
      if (e.target.closest('.nav-toggle')) { setOpen(p, !isOpen(p), false); return; }
      if (!isOpen(p)) { return; }
      if (e.target.closest('[data-nav-close]')) { setOpen(p, false, true); return; }
      var link = e.target.closest('a[href]');
      if (link && p.drawer.contains(link)) { setOpen(p, false, false); }
    });

    document.addEventListener('keydown', function (e) {
      var p = parts();
      if (!isOpen(p)) { return; }
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        setOpen(p, false, true);
        return;
      }
      if (e.key === 'Tab') {
        var items = qsa(focusable, p.drawer).filter(function (el) { return el.offsetParent !== null; });
        if (!items.length) { return; }
        var firstEl = items[0];
        var lastEl = items[items.length - 1];
        if (e.shiftKey && (document.activeElement === firstEl || !p.drawer.contains(document.activeElement))) {
          e.preventDefault(); lastEl.focus();
        } else if (!e.shiftKey && (document.activeElement === lastEl || !p.drawer.contains(document.activeElement))) {
          e.preventDefault(); firstEl.focus();
        }
      }
    });

    if (window.matchMedia) {
      var desktop = window.matchMedia('(min-width: 1001px)');
      var onChange = function (mq) { var p = parts(); if (mq.matches && isOpen(p)) { setOpen(p, false, false); } };
      if (desktop.addEventListener) { desktop.addEventListener('change', onChange); }
      else if (desktop.addListener) { desktop.addListener(onChange); }
    }
    document.addEventListener('shopify:section:unload', function () {
      document.documentElement.classList.remove('nav-open');
    });
  }());

  /* ---------------------------------------------------------
     Desktop dropdowns (header "menu" mode). Hover and focus-within are
     CSS; this adds tap-to-open on touch screens.
     --------------------------------------------------------- */
  (function navDrops() {
    var items = qsa('.site-nav__item.has-drop');
    if (!items.length) { return; }

    function isShown(item) {
      if (item.classList.contains('is-dismissed')) { return false; }
      return item.classList.contains('open') || item.matches(':hover') || item.contains(document.activeElement);
    }
    function sync(item) {
      var link = qs('.site-nav__link', item);
      if (link) { link.setAttribute('aria-expanded', isShown(item) ? 'true' : 'false'); }
    }
    function syncSoon(item) { window.setTimeout(function () { sync(item); }, 0); }

    items.forEach(function (item) {
      var link = qs('.site-nav__link', item);
      if (!link) { return; }
      link.setAttribute('aria-expanded', 'false');
      link.addEventListener('click', function (e) {
        if (window.matchMedia && window.matchMedia('(hover: none)').matches && !item.classList.contains('open')) {
          e.preventDefault();
          items.forEach(function (o) { o.classList.remove('open'); sync(o); });
          item.classList.remove('is-dismissed');
          item.classList.add('open');
          sync(item);
        }
      });
      item.addEventListener('mouseenter', function () { item.classList.remove('is-dismissed'); sync(item); });
      item.addEventListener('mouseleave', function () { item.classList.remove('is-dismissed'); syncSoon(item); });
      item.addEventListener('focusin', function () { sync(item); });
      item.addEventListener('focusout', function () {
        window.setTimeout(function () {
          if (!item.contains(document.activeElement)) { item.classList.remove('is-dismissed', 'open'); }
          sync(item);
        }, 0);
      });
      /* Escape closes the open dropdown and returns focus to its parent link (WCAG 1.4.13) */
      item.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' && e.key !== 'Esc') { return; }
        if (!isShown(item)) { return; }
        e.preventDefault();
        item.classList.remove('open');
        item.classList.add('is-dismissed');
        link.focus();
        sync(item);
      });
    });
    document.addEventListener('click', function (e) {
      if (!e.target.closest || !e.target.closest('.site-nav__item.has-drop')) {
        items.forEach(function (o) { o.classList.remove('open'); sync(o); });
      }
    });
  }());

  /* ---------------------------------------------------------
     Announcement bar rotator. data-announce is milliseconds (values under
     100 are treated as seconds). Pauses while hovered or focused, stops with
     the pause button, and never rotates under reduced motion.
     --------------------------------------------------------- */
  (function announce() {
    var bar = qs('[data-announce]');
    if (!bar) { return; }
    var slides = qsa('.announce-slide', bar);
    if (slides.length < 2) { return; }
    var toggle = qs('[data-announce-pause]', bar);
    /* Reduced motion: no rotation, the first message stays (the pause button is not needed) */
    if (reducedMotion()) {
      if (toggle) { toggle.hidden = true; }
      return;
    }
    var raw = parseInt(bar.getAttribute('data-announce'), 10) || 5000;
    var delay = Math.max(2500, raw < 100 ? raw * 1000 : raw);
    var i = 0;
    var paused = false;
    var stopped = false; /* set by the visible pause button (WCAG 2.2.2) */
    if (toggle) {
      toggle.hidden = false;
      toggle.addEventListener('click', function () {
        stopped = !stopped;
        toggle.setAttribute('aria-pressed', stopped ? 'true' : 'false');
      });
    }
    bar.addEventListener('mouseenter', function () { paused = true; });
    bar.addEventListener('mouseleave', function () { paused = false; });
    bar.addEventListener('focusin', function () { paused = true; });
    bar.addEventListener('focusout', function () { paused = false; });
    setInterval(function () {
      if (paused || stopped) { return; }
      slides[i].classList.remove('on');
      i = (i + 1) % slides.length;
      slides[i].classList.add('on');
    }, delay);
  }());

  /* ---------------------------------------------------------
     Quantity steppers (product page and cart): .qty > [data-qty="up"|"down"]
     --------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-qty]') : null;
    if (!btn) { return; }
    var box = btn.closest('.qty');
    var input = box ? qs('input', box) : null;
    if (!input) { return; }
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) { min = 0; }
    var val = parseInt(input.value, 10) || min;
    val += btn.getAttribute('data-qty') === 'up' ? 1 : -1;
    if (val < min) { val = min; }
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------------------------------------------------------
     Product gallery: [data-thumb] switches the image inside [data-stage]
     --------------------------------------------------------- */
  (function gallery() {
    var stage = qs('[data-stage]');
    if (!stage) { return; }
    var images = qsa('img', stage);
    qsa('[data-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var n = parseInt(thumb.getAttribute('data-thumb'), 10);
        images.forEach(function (img, j) { img.hidden = j !== n; });
        qsa('[data-thumb]').forEach(function (t) { t.classList.toggle('on', t === thumb); });
      });
    });
  }());

  /* ---------------------------------------------------------
     Variant selection: price, availability and the form's variant id
     (#variant-data JSON, #product-form, select[data-option-index], #variant-id,
     #pdp-price-now, button[name="add"][data-add][data-sold], #pdp-buy-now)
     --------------------------------------------------------- */
  (function variants() {
    var data = qs('#variant-data');
    var form = qs('#product-form');
    if (!data || !form) { return; }

    var list;
    try { list = JSON.parse(data.textContent); } catch (err) { return; }

    var selects = qsa('select[data-option-index]', form);
    if (!selects.length) { return; }

    var idField = qs('#variant-id', form);
    var priceEl = qs('#pdp-price-now');
    var button = form.querySelector('button[name="add"]');
    var buyNow = qs('#pdp-buy-now');
    var labels = {
      add: button ? button.getAttribute('data-add') : '',
      sold: button ? button.getAttribute('data-sold') : ''
    };

    form.addEventListener('change', function () {
      var chosen = selects.map(function (s) { return s.value; });
      var match = list.filter(function (v) {
        return chosen.every(function (val, n) { return v.options[n] === val; });
      })[0];
      if (!match) { return; }

      if (idField) { idField.value = match.id; }
      if (priceEl) { priceEl.textContent = match.price; }
      if (button) {
        button.disabled = !match.available;
        button.textContent = match.available ? labels.add : labels.sold;
      }
      if (buyNow) {
        buyNow.disabled = !match.available;
        buyNow.classList.toggle('is-disabled', !match.available);
      }
    });
  }());

  /* ---------------------------------------------------------
     Related-products rail on the product page:
     .rail-outer > .rail + .rail-arrow.prev / .rail-arrow.next
     --------------------------------------------------------- */
  qsa('.rail-outer').forEach(function (outer) {
    var rail = qs('.rail', outer);
    var prev = qs('.rail-arrow.prev', outer);
    var next = qs('.rail-arrow.next', outer);
    if (!rail) { return; }

    function step() {
      var first = rail.firstElementChild;
      if (!first) { return 240; }
      var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }
    function sync() {
      var max = rail.scrollWidth - rail.clientWidth - 2;
      if (prev) { prev.disabled = rail.scrollLeft <= 2; }
      if (next) { next.disabled = rail.scrollLeft >= max; }
    }
    if (prev) { prev.addEventListener('click', function () { rail.scrollBy({ left: -step() * 2, behavior: 'smooth' }); }); }
    if (next) { next.addEventListener('click', function () { rail.scrollBy({ left: step() * 2, behavior: 'smooth' }); }); }
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    sync();
  });

  /* ---------------------------------------------------------
     Wishlist hearts ([data-wish]) — only rendered by product-card and
     main-product when Theme settings > Shop — behaviour > wishlist is on.
     Stored in this browser only; nothing leaves the device.
     --------------------------------------------------------- */
  (function wishlist() {
    if (!qs('[data-wish]')) { return; }
    var KEY = 'sj_wishlist';
    function read() {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (err) { return []; }
    }
    function write(v) {
      try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (err) { /* private mode */ }
    }
    function paint() {
      var saved = read();
      qsa('[data-wish]').forEach(function (btn) {
        var on = saved.indexOf(btn.getAttribute('data-wish')) !== -1;
        btn.classList.toggle('on', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-wish]') : null;
      if (!btn) { return; }
      e.preventDefault();
      e.stopPropagation();
      var id = btn.getAttribute('data-wish');
      var saved = read();
      var at = saved.indexOf(id);
      if (at === -1) { saved.push(id); } else { saved.splice(at, 1); }
      write(saved);
      paint();
    });
    paint();
  }());

  /* Anything added to the page by the blocks above is picked up here. */
  reveal(document);
}());

/* === SECTION PARTS APPENDED BELOW BY ASSEMBLER === */

/* ============================================================
   SECTION PART: media-news  (source: build/js/media-news.js)
   ============================================================ */
try {
/* ---------------------------------------------------------
   Media & News (sections/media-news.liquid)
   The Facebook Page Plugin iframe lays its timeline out at the width
   in its URL (500px) and crops anything wider than its box. On phones
   and in narrow masonry columns that cuts posts off, so this rewrites
   the width parameter to the card's real width (Facebook allows
   180-500px) before the lazy iframe loads, and again after a resize.
   Without JavaScript the plugin still loads at 500px.
   --------------------------------------------------------- */
(function () {
  'use strict';
  if (!window.JZTheme) { return; }

  var SELECTOR = 'iframe[data-media-news-fb]';
  var MIN_WIDTH = 180;
  var MAX_WIDTH = 500;
  var GROW_STEP = 40; /* only reload a wider feed when it gains this much room */

  function fit(frame) {
    var box = frame.parentNode;
    if (!box || !box.clientWidth) { return; }
    var src = frame.getAttribute('src') || '';
    var match = src.match(/[?&]width=(\d+)/);
    if (!match) { return; }

    var have = parseInt(match[1], 10);
    var want = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.floor(box.clientWidth)));
    /* shrink whenever the feed would be cropped; grow only by a useful amount */
    if (want === have || (want > have && want - have < GROW_STEP)) { return; }

    frame.setAttribute('src', src.replace(/([?&]width=)\d+/, '$1' + want));
    frame.setAttribute('width', String(want));
  }

  var timer = null;
  var lastWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    /* mobile browsers fire resize when the address bar hides: ignore height-only changes */
    if (window.innerWidth === lastWidth) { return; }
    lastWidth = window.innerWidth;
    window.clearTimeout(timer);
    timer = window.setTimeout(function () {
      window.JZTheme.qsa(SELECTOR).forEach(fit);
    }, 300);
  }, { passive: true });

  window.JZTheme.init(SELECTOR, fit);
}());

/* ---------------------------------------------------------
   Newsletter result: Shopify reloads the page after the sign-up posts,
   and a live region that is already in the page at load is not announced.
   Move focus to the success or error message (it has tabindex="-1") so
   keyboard and screen reader users hear the outcome, as booking-form does.
   --------------------------------------------------------- */
(function () {
  'use strict';
  if (!window.JZTheme) { return; }
  window.JZTheme.init('.media-news__form-status', function (el) {
    window.setTimeout(function () {
      try { el.focus({ preventScroll: false }); } catch (err) { el.focus(); }
    }, 60);
  });
}());
} catch (err) {
  if (window.console && console.error) { console.error('[JZ theme] media-news part failed', err); }
}

/* ============================================================
   SECTION PART: faq  (source: build/js/faq.js)
   ============================================================ */
try {
/* FAQ (sections/faq.liquid) — theme editor helper only.
   The accordion is native <details> and needs no JavaScript. In the theme editor,
   selecting a Question block opens it (and shows its list if the reveal has not
   run yet) so the answer being edited is visible. The shopify:block:select event
   only fires inside the editor, so this does nothing on the live site. */
(function () {
  'use strict';
  if (typeof document === 'undefined' || !document.addEventListener) return;

  document.addEventListener('shopify:block:select', function (event) {
    var item = event.target;
    if (!item || typeof item.matches !== 'function' || !item.matches('details.faq__item')) return;
    item.open = true;
    var list = typeof item.closest === 'function' ? item.closest('.faq__list') : null;
    if (list && list.classList) list.classList.add('in');
  });
}());
} catch (err) {
  if (window.console && console.error) { console.error('[JZ theme] faq part failed', err); }
}

/* ============================================================
   SECTION PART: booking-form  (source: build/js/booking-form.js)
   ============================================================ */
try {
/* ------------------------------------------------------------
   Booking form (sections/booking-form.liquid)
   1. After Shopify reloads the page with a success or error message,
      move focus to that message so keyboard and screen reader users
      hear the outcome.
   2. Visitors often type "mybrand.com.au" into the url field, which
      the browser rejects. Add https:// before validation runs.
   ------------------------------------------------------------ */
(function () {
  'use strict';

  var BARE_DOMAIN = /^[^\s\/:@]+\.[^\s]+$/;
  var HAS_SCHEME = /^[a-z][a-z0-9+.\-]*:/i;

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function fixUrl(input) {
    var v = (input.value || '').trim();
    if (!v || HAS_SCHEME.test(v)) { return; }
    if (v.indexOf('//') === 0) { v = v.slice(2); }
    if (BARE_DOMAIN.test(v)) { input.value = 'https://' + v; }
  }

  function setup(root) {
    if (!root || root.hasAttribute('data-booking-ready')) { return; }
    root.setAttribute('data-booking-ready', '');

    var status = root.querySelector('[data-booking-status]');
    if (status) {
      var focusStatus = function () {
        try { status.focus({ preventScroll: false }); } catch (err) { status.focus(); }
      };
      /* Short delay so the browser's jump to #booking-form happens first. */
      window.setTimeout(focusStatus, 60);
    }

    var urls = qsa('input[data-booking-url]', root);
    if (!urls.length) { return; }
    urls.forEach(function (input) {
      input.addEventListener('blur', function () { fixUrl(input); });
      input.addEventListener('change', function () { fixUrl(input); });
    });
    /* A submit click (or Enter, which fires a click on the submit button)
       runs before the browser checks the fields. */
    qsa('[data-booking-submit]', root).forEach(function (btn) {
      btn.addEventListener('click', function () { urls.forEach(fixUrl); });
    });
  }

  var theme = window.JZTheme;
  if (theme && typeof theme.init === 'function') {
    theme.init('[data-booking-form]', setup);
  } else {
    var run = function () { qsa('[data-booking-form]').forEach(setup); };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }
}());
} catch (err) {
  if (window.console && console.error) { console.error('[JZ theme] booking-form part failed', err); }
}
